import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import AuditLogTable from './components/AuditLogTable';
import StatsCards from './components/StatsCards';
import TimelineView from './components/TimelineView';
import { auditLogsService } from '../../services/supabaseService';

const AuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'timeline'
  const [totalCount, setTotalCount] = useState(0);
  const [statistics, setStatistics] = useState({
    totalLogs: 0,
    byAction: {},
    byEntity: {},
    recentActivity: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    actionType: 'all',
    entityType: 'all',
    startDate: '',
    endDate: '',
    searchTerm: '',
    limit: 100
  });

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Audit Log', path: '/audit-log' }
  ];

  // Load audit logs on mount and when filters change
  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  // Load statistics on mount
  useEffect(() => {
    loadStatistics();
  }, []);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const result = await auditLogsService?.getFiltered(filters);
      setAuditLogs(result?.data || []);
      setTotalCount(result?.count || 0);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await auditLogsService?.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExportCSV = async () => {
    try {
      const csvContent = await auditLogsService?.exportToCSV(filters);
      if (!csvContent) {
        alert('No data to export');
        return;
      }

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `audit_log_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export audit log');
    }
  };

  const handleRefresh = () => {
    loadAuditLogs();
    loadStatistics();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Audit Log
            </h1>
            <p className="font-body text-base text-muted-foreground">
              Comprehensive tracking of all system changes and user activities
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={18} />
              <span className="hidden md:inline">Refresh</span>
            </Button>
            
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="Download" size={18} />
              <span className="hidden md:inline">Export CSV</span>
            </Button>
            
            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-md transition-smooth ${
                  viewMode === 'table' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Table" size={18} />
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-2 rounded-md transition-smooth ${
                  viewMode === 'timeline' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Clock" size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <StatsCards statistics={statistics} totalCount={totalCount} />

        {/* Filter Panel */}
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} />

        {/* Results Count */}
        <div className="mb-4 flex items-center justify-between">
          <p className="font-body text-sm text-muted-foreground">
            Showing {auditLogs?.length} of {totalCount} audit entries
          </p>
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
        </div>

        {/* Content View */}
        {viewMode === 'table' ? (
          <AuditLogTable auditLogs={auditLogs} loading={loading} />
        ) : (
          <TimelineView auditLogs={auditLogs} loading={loading} />
        )}
      </main>
    </div>
  );
};

export default AuditLog;